import apiClient from '../config/api';

/**
 * Utility function to convert data to CSV format
 */
const convertToCSV = (data, headers) => {
  if (!data || data.length === 0) {
    return '';
  }

  // Create header row
  const headerRow = headers.map(h => `"${h}"`).join(',');
  
  // Create data rows
  const dataRows = data.map(row => {
    return headers.map(header => {
      const value = row[header] || '';
      // Escape quotes and wrap in quotes
      return `"${String(value).replace(/"/g, '""')}"`;
    }).join(',');
  });

  return [headerRow, ...dataRows].join('\n');
};

/**
 * Utility function to download CSV file
 */
const downloadCSV = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const reportService = {
  /**
   * Get downtime report data
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @param {number} machineId - Optional machine ID
   * @returns {Promise} Downtime report data
   */
  getDowntimeReport: async (startDate = null, endDate = null, machineId = null) => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate.toISOString();
      if (endDate) params.endDate = endDate.toISOString();
      if (machineId) params.machineId = machineId;

      // Fetch OEE logs which contain downtime data
      const response = await apiClient.get('/oeelog', { params });
      const logs = response.data || [];

      // Transform to downtime report format
      const reportData = logs.map(log => ({
        'Date': log.shiftDate ? new Date(log.shiftDate).toLocaleDateString() : '-',
        'Machine': log.machine?.machineName || log.machineCode || '-',
        'Machine Code': log.machine?.machineCode || '-',
        'Planned Downtime (min)': log.plannedDowntime || 0,
        'Unplanned Downtime (min)': log.unplannedDowntime || 0,
        'Total Downtime (min)': (log.plannedDowntime || 0) + (log.unplannedDowntime || 0),
        'Shift': log.shiftCode || '-',
        'Plant': log.plant?.plantName || '-',
        'Line': log.line?.lineName || '-'
      }));

      return reportData;
    } catch (error) {
      console.error('Error fetching downtime report:', error);
      throw error;
    }
  },

  /**
   * Get MTBR (Mean Time Between Repair) report data
   * MTBR = Total Operating Time / Number of Repairs
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @param {number} machineId - Optional machine ID
   * @returns {Promise} MTBR report data
   */
  getMTBRReport: async (startDate = null, endDate = null, machineId = null) => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate.toISOString();
      if (endDate) params.endDate = endDate.toISOString();
      if (machineId) params.machineId = machineId;

      // Call the new backend MTBR endpoint
      const response = await apiClient.get('/mtbr', { params });
      const result = response.data;

      // Transform to report format
      if (machineId && result.machineName) {
        // Single machine result
        return [{
          'Machine': result.machineName || '-',
          'Machine Code': result.machineCode || '-',
          'Total Downtime (hours)': result.totalTimeHours?.toFixed(2) || '0.00',
          'Number of Breakdowns': result.numberOfBreakdowns || 0,
          'MTBR (hours)': result.mtbrHours?.toFixed(2) || '0.00',
          'MTBR (min)': result.mtbrMinutes?.toFixed(2) || '0.00'
        }];
      } else if (result.machineWiseMTBR && result.machineWiseMTBR.length > 0) {
        // Multiple machines
        return result.machineWiseMTBR.map(machine => ({
          'Machine': machine.machineName || '-',
          'Machine Code': machine.machineCode || '-',
          'Total Downtime (hours)': machine.totalTimeHours?.toFixed(2) || '0.00',
          'Number of Breakdowns': machine.numberOfBreakdowns || 0,
          'MTBR (hours)': machine.mtbrHours?.toFixed(2) || '0.00',
          'MTBR (min)': machine.mtbrMinutes?.toFixed(2) || '0.00'
        }));
      } else {
        // Aggregate result
        return [{
          'Total Downtime (hours)': result.totalTimeHours?.toFixed(2) || '0.00',
          'Number of Breakdowns': result.numberOfBreakdowns || 0,
          'MTBR (hours)': result.mtbrHours?.toFixed(2) || '0.00',
          'MTBR (min)': result.mtbrMinutes?.toFixed(2) || '0.00'
        }];
      }
    } catch (error) {
      console.error('Error fetching MTBR report:', error);
      throw error;
    }
  },

  /**
   * Get MTBF (Mean Time Between Failure) report data
   * MTBF = Total Operating Time / Number of Failures
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @param {number} machineId - Optional machine ID
   * @returns {Promise} MTBF report data
   */
  getMTBFReport: async (startDate = null, endDate = null, machineId = null) => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate.toISOString();
      if (endDate) params.endDate = endDate.toISOString();
      if (machineId) params.machineId = machineId;

      // Call the new backend MTBF endpoint
      const response = await apiClient.get('/mtbf', { params });
      const result = response.data;

      // Transform to report format
      if (machineId && result.machineName) {
        // Single machine result
        return [{
          'Machine': result.machineName || '-',
          'Machine Code': result.machineCode || '-',
          'Total Operating Time (hours)': result.totalOperatingTimeHours?.toFixed(2) || '0.00',
          'Number of Failures': result.numberOfFailures || 0,
          'MTBF (hours)': result.mtbfHours?.toFixed(2) || '0.00',
          'MTBF (min)': result.mtbfMinutes?.toFixed(2) || '0.00'
        }];
      } else if (result.machineWiseMTBF && result.machineWiseMTBF.length > 0) {
        // Multiple machines
        return result.machineWiseMTBF.map(machine => ({
          'Machine': machine.machineName || '-',
          'Machine Code': machine.machineCode || '-',
          'Total Operating Time (hours)': machine.totalOperatingTimeHours?.toFixed(2) || '0.00',
          'Number of Failures': machine.numberOfFailures || 0,
          'MTBF (hours)': machine.mtbfHours?.toFixed(2) || '0.00',
          'MTBF (min)': machine.mtbfMinutes?.toFixed(2) || '0.00'
        }));
      } else {
        // Aggregate result
        return [{
          'Total Operating Time (hours)': result.totalOperatingTimeHours?.toFixed(2) || '0.00',
          'Number of Failures': result.numberOfFailures || 0,
          'MTBF (hours)': result.mtbfHours?.toFixed(2) || '0.00',
          'MTBF (min)': result.mtbfMinutes?.toFixed(2) || '0.00'
        }];
      }
    } catch (error) {
      console.error('Error fetching MTBF report:', error);
      throw error;
    }
  },

  /**
   * Export report data to CSV
   * @param {Array} data - Report data array
   * @param {Array} headers - Column headers
   * @param {string} filename - Filename for download
   */
  exportToCSV: (data, headers, filename) => {
    if (!data || data.length === 0) {
      alert('No data to export');
      return;
    }

    const csvContent = convertToCSV(data, headers);
    downloadCSV(csvContent, filename);
  }
};

export default reportService;

